/**
 * CR310: S0 Scan Settings v2 - Candidate Signals Tab (Tab 3)
 * Candidate page selection scoring and signals
 */

import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Shield, Target } from 'lucide-react';
import { CandidateSignalBuilder } from '@/components/admin/flyer-v2/CandidateSignalBuilder';
import type { BatchRunFlyerConfig } from '../types';
import type { CandidateSignal } from '@/lib/flyer-pipeline-v2/types';

interface CandidateSignalsTabProps {
  config: BatchRunFlyerConfig;
  updateConfig: <K extends keyof BatchRunFlyerConfig>(key: K, value: BatchRunFlyerConfig[K]) => void;
  disabled?: boolean;
}

export function CandidateSignalsTab({
  config,
  updateConfig,
  disabled = false,
}: CandidateSignalsTabProps) {
  const activeSignalsCount = config.signals.filter(s => s.enabled).length;

  return (
    <div className="space-y-6">
      {/* Threshold Settings */}
      <div className="space-y-4">
        <div className="flex items-center gap-2 text-sm font-medium">
          <Target className="h-4 w-4" />
          Candidate Threshold
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-xs">Score Threshold</Label>
            <Input
              type="number"
              min={0}
              step={0.5}
              value={config.candidateThreshold}
              onChange={(e) => updateConfig('candidateThreshold', parseFloat(e.target.value) || 12)}
              disabled={disabled}
              className="h-8 text-xs"
            />
            <p className="text-xs text-muted-foreground">
              Pages scoring ≥ this value are sent to AI deep scan
            </p>
          </div>
        </div>
      </div>

      {/* Signals Builder */}
      <div className="space-y-3 pt-4 border-t">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm font-medium">
            <Shield className="h-4 w-4" />
            Scoring Signals
          </div>
          <Badge variant="outline" className="text-xs">
            {activeSignalsCount} active
          </Badge>
        </div>
        
        <CandidateSignalBuilder
          signals={config.signals}
          onChange={(signals: CandidateSignal[]) => updateConfig('signals', signals)}
          disabled={disabled}
        />
      </div>
    </div>
  );
}
